import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { merge, take } from 'rxjs';
import { delay, skip } from 'rxjs/operators';
import {
  DayOfWeek,
  HappyHoursPeriodNowModel,
  HappyHoursService,
  parseTimeObjectFromString,
  scheduleAt,
  TimeString,
  weeklyPeriodScheduler,
} from '@core';
import { AvailabilitySchema } from '@app/schemas/availability';

@Injectable({
  providedIn: 'root',
})
export class AvailabilityService {

  private readonly translateService = inject(TranslateService);
  private readonly happyHoursService = inject(HappyHoursService);

  public buildSchedule$(availabilities: AvailabilitySchema[]) {
    return merge(
      this.translateService.onLangChange,
      this.happyHoursService.isNow$.pipe(skip(1)),
      ...(
        availabilities.map((availability) => {
          return (
            availability.schedule ?? []
          ).map((schedule) => {
            const now = new Date();

            if (schedule.endDate && now > new Date(schedule.endDate)) {
              return undefined;
            }

            if (schedule.startDate && now < new Date(schedule.startDate)) {
              const startDate = new Date(schedule.startDate);
              return scheduleAt(startDate);
            }

            const weeklyPeriods = (
              schedule.times ?? []
            ).map((timeRange) => {
              return {
                startDay: timeRange.startDay as DayOfWeek,
                startTime: timeRange.startTime as TimeString,
                endDay: timeRange.endDay as DayOfWeek,
                endTime: timeRange.endTime as TimeString,
                data: undefined,
              };
            });

            if (schedule.endDate && now < new Date(schedule.endDate)) {
              const endDate = new Date(schedule.endDate);

              if (weeklyPeriods.length > 0) {
                return merge(
                  weeklyPeriodScheduler(weeklyPeriods, false),
                  scheduleAt(endDate),
                );
              }

              return scheduleAt(endDate);
            }

            return weeklyPeriodScheduler(weeklyPeriods, false);
          }).filter(Boolean);
        }).flat()
      ),
    ).pipe(
      take(1),
      delay(500),
    );
  }

  public checkAvailability(availability: AvailabilitySchema): boolean {
    return (
      availability.enabled &&
      this.checkAvailabilitySchedule(availability) &&
      this.checkAvailabilityLanguage(availability) &&
      this.checkAvailabilityHappyHour(availability)
    );
  }

  public checkAvailabilitySchedule(availability: AvailabilitySchema): boolean {
    if (!availability.schedule || availability.schedule.length === 0) {
      return true;
    }

    const now = new Date();

    for (const scheduleItem of availability.schedule) {
      if (scheduleItem.startDate) {
        const startDate = new Date(scheduleItem.startDate);

        if (now < startDate) {
          continue;
        }
      }

      if (scheduleItem.endDate) {
        const endDate = new Date(scheduleItem.endDate);

        if (now > endDate) {
          continue;
        }
      }

      if (scheduleItem.times && scheduleItem.times.length > 0) {
        let timeMatch = false;

        for (const timeRange of scheduleItem.times) {
          if (timeRange.startDay > now.getDay() || timeRange.endDay < now.getDay()) {
            continue;
          }

          const startTimeDate = new Date(now);
          if (timeRange.startTime) {
            const startTimeObject = parseTimeObjectFromString(timeRange.startTime);
            startTimeDate.setHours(startTimeObject.hour, startTimeObject.minute, startTimeObject.second, 0);
          }

          const endTimeDate = new Date(now);
          if (timeRange.endTime) {
            const endTimeObject = parseTimeObjectFromString(timeRange.endTime);
            endTimeDate.setHours(endTimeObject.hour, endTimeObject.minute, endTimeObject.second, 0);
          }

          if (now >= startTimeDate && now <= endTimeDate) {
            timeMatch = true;
            break;
          }
        }

        if (!timeMatch) {
          continue;
        }
      }

      return true;
    }

    return false;
  }

  public checkAvailabilityLanguage(availability: AvailabilitySchema): boolean {
    if (!availability.languages || availability.languages.length === 0) {
      return true;
    }

    return availability.languages.includes(this.translateService.getCurrentLang());
  }

  public checkAvailabilityHappyHour(availability: AvailabilitySchema): boolean {
    if (!availability.happyHour) {
      return true;
    }

    const isNow = this.happyHoursService.status$.getValue() instanceof HappyHoursPeriodNowModel;

    if (availability.happyHour === 'during') {
      return isNow;
    }

    return !isNow;
  }

}
